import { useIntl } from "react-intl";

import { SIGNUP_CTA_URL } from "@/components/wpl/pricing/src/price-finder/constants";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features";
import { Jfind1384PriceCard } from "@/components/wpl/pricing/src/price-finder-jfind1384/jfind-1384-price-card";
import { jiraOfferingSkus } from "@/components/wpl/pricing/src/price-finder-v3/configs";
import { useSelector } from "@/components/wpl/pricing/src/price-finder-v3/hooks.ts";
import * as selectors from "@/components/wpl/pricing/src/price-finder-v3/selectors";
import {
    usePricingPlanComparison,
    usePricingPlanCta
} from "@/components/wpl/pricing/src/pricing-content-context";
import { useLocalizedHref } from "@/libraries/web-framework";

import {
    DATA_PRODUCT_KEY,
    SignupUrlSuffix,
    PriceDisplayMessages
} from "./utils/constants";

export const PlanPremium = () => {
    const intl = useIntl();

    const billingCycle = useSelector(selectors.getBillingCycle);
    const isUnitCountAboveMaxThreshold = useSelector((ctx) =>
        selectors.isUnitCountAboveMaxThreshold(ctx, jiraOfferingSkus.premium)
    );

    let price = useSelector((ctx) =>
        selectors.getRoundedFormattedPlanPrice(ctx, jiraOfferingSkus.premium)
    );

    let priceDisplayMessage: ReactNode | undefined = undefined;
    let isCtaDisabled = false;
    if (isUnitCountAboveMaxThreshold) {
        isCtaDisabled = true;
        price = null;
        priceDisplayMessage = intl.formatMessage(
            PriceDisplayMessages.exceedsUserLimit
        );
    }

    const recommended = useSelector((ctx) =>
        selectors.isOfferingRecommended(ctx, jiraOfferingSkus.premium)
    );
    const unitCount = useSelector((ctx) => selectors.getUnitCount(ctx));
    const unitTierLowerBound = useSelector((ctx) =>
        selectors.getUnitTierLowerBound(ctx, jiraOfferingSkus.premium)
    );
    const unitTierUpperBound = useSelector((ctx) =>
        selectors.getUnitTierUpperBound(ctx, jiraOfferingSkus.premium)
    );

    // todo: localized strings (https://hello.jira.atlassian.cloud/browse/JFIND-1475)
    const premiumContent = usePricingPlanComparison("Premium");
    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        { premium: premiumContent },
        "premium"
    );
    const getPricingPlanCta = usePricingPlanCta();
    const contentfulCta = getPricingPlanCta({ planName: "Premium" });
    const ctaText = intl.formatMessage({
        id: "Pricing.PriceCard.Premium.CtaText",
        defaultMessage: "Get it free"
    });
    const localizedSignupURL = useLocalizedHref(SIGNUP_CTA_URL);
    const ctaUrl = `${localizedSignupURL}${SignupUrlSuffix["premium"]}`;

    const planDescription = intl.formatMessage({
        id: "Pricing.PriceCard.Premium.Description",
        defaultMessage: "For planning across teams, and AI-powered productivity"
    });

    const freeTag = price && billingCycle === "MONTHLY" ? "30days" : undefined;
    const priceSlash =
        price && billingCycle === "MONTHLY" ? "purple" : undefined;

    const numberFormatLocale = useSelector(selectors.getNumberFormatLocale);

    return (
        <Jfind1384PriceCard
            last={true}
            highlighted={recommended}
            isRecommendedPlan={recommended}
            planName="Premium"
            description={planDescription}
            planFeatureMessages={premiumPlanFeatureMessages}
            ctaId={"planCtaPremium"}
            ctaText={ctaText}
            ctaUrl={contentfulCta?.ctaUrl ?? ctaUrl}
            billingCycle={billingCycle}
            dataProductKey={DATA_PRODUCT_KEY}
            price={price}
            priceDisplayMessage={priceDisplayMessage}
            isCtaDisabled={isCtaDisabled}
            priceCalcIdx={0}
            edition="premium"
            freeTag={freeTag}
            priceSlash={priceSlash}
            adjacentLowerTierPlanName={"Standard"}
            numberFormatLocale={numberFormatLocale}
            unitCount={unitCount}
            unitTierLowerBound={unitTierLowerBound}
            unitTierUpperBound={unitTierUpperBound}
        />
    );
};
